import React, { useState, useEffect, useCallback } from 'react';

const OnboardingTour = ({ steps, tourId, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(true);

  const step = steps[currentStep];

  const updatePosition = useCallback(() => {
    if (!step?.target) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('resize', updatePosition);
    const interval = setInterval(updatePosition, 500);
    return () => {
      window.removeEventListener('resize', updatePosition);
      clearInterval(interval);
    };
  }, [currentStep, updatePosition]);

  const next = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setVisible(false);
      onComplete?.(tourId);
    }
  };

  const skip = () => {
    setVisible(false);
    onSkip?.(tourId);
  };

  if (!visible || !steps.length) return null;

  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (!targetRect) {
      return { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    const padding = 12;
    const tooltipWidth = 340;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    let top, left;
    const placement = step.placement || 'bottom';

    if (placement === 'bottom') {
      top = targetRect.top + targetRect.height + padding;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
    } else if (placement === 'top') {
      top = targetRect.top - padding - 180;
      left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
    } else if (placement === 'right') {
      top = targetRect.top + targetRect.height / 2 - 80;
      left = targetRect.left + targetRect.width + padding;
    } else {
      top = targetRect.top + targetRect.height / 2 - 80;
      left = targetRect.left - tooltipWidth - padding;
    }

    // Clamp to viewport
    if (left < 10) left = 10;
    if (left + tooltipWidth > viewportW - 10) left = viewportW - tooltipWidth - 10;
    if (top < 10) top = 10;
    if (top > viewportH - 200) top = viewportH - 200;

    return { position: 'fixed', top: `${top}px`, left: `${left}px`, width: `${tooltipWidth}px` };
  };

  return (
    <div className="fixed inset-0 z-[9999]" data-testid={`tour-${tourId}`}>
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={skip} />

      {/* Spotlight cutout */}
      {targetRect && (
        <div className="absolute pointer-events-none transition-all duration-300 ease-out"
          style={{
            top: targetRect.top - 6,
            left: targetRect.left - 6,
            width: targetRect.width + 12,
            height: targetRect.height + 12,
            borderRadius: '12px',
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
            zIndex: 10000,
          }}
        >
          <div className="absolute inset-0 rounded-xl border-2 border-purple-400 animate-pulse" />
        </div>
      )}

      {/* Tooltip */}
      <div style={getTooltipStyle()} className="z-[10001] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-bottom-3" data-testid="tour-tooltip">
        {/* Progress bar */}
        <div className="h-1 bg-gray-100">
          <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }} />
        </div>

        <div className="p-5">
          {/* Step icon */}
          {step.icon && (
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mb-3">
              <span className="text-lg" dangerouslySetInnerHTML={{ __html: step.icon }} />
            </div>
          )}

          <h3 className="text-base font-bold text-gray-900 mb-1.5" data-testid="tour-step-title">{step.title}</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>

          {/* Actions */}
          <div className="flex items-center justify-between mt-5">
            <button onClick={skip} data-testid="tour-skip"
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
              Saltar tour
            </button>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">{currentStep + 1} / {steps.length}</span>
              <button onClick={next} data-testid="tour-next"
                className="px-5 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg">
                {currentStep < steps.length - 1 ? 'Siguiente' : 'Entendido'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
